import type { Controls } from '../core/device';
import { clamp, lerp } from '../core/math';
import type { Random } from '../core/Random';
import { banner } from './draw';
import type { MiniGame, MiniInput } from './types';

/** Lane coordinates: u = left/right (-1..1), d = distance down the lane (0 = you, 1 = far end). */
export const BOARD = { halfWidth: 0.34, front: 0.74, back: 0.97, holeD: 0.9, holeU: 0.085, holeR: 0.042 };
const BAGS = 4;
const MAX_FRAMES = 4;
export const WIN_SCORE = 11;

export type Thrower = 'you' | 'ricky';

export interface Bag {
  who: Thrower;
  u: number;
  d: number;
  /** 3 = in the hole, 1 = on the board, 0 = in the grass. */
  points: number;
}

/** Where a throw lands, from aim (-1..1) and power (0..1), before it slides. */
export function landing(aim: number, power: number): { u: number; d: number } {
  return { u: aim * 0.5, d: 0.45 + power * 0.6 };
}

const inHole = (u: number, d: number): boolean =>
  (u / BOARD.holeU) ** 2 + ((d - BOARD.holeD) / BOARD.holeR) ** 2 <= 1;
const onBoard = (u: number, d: number): boolean =>
  Math.abs(u) <= BOARD.halfWidth && d >= BOARD.front && d <= BOARD.back;

/** Resolves a bag that lands at (u, d) and slides `slide` farther: scored the way cornhole is. */
export function settle(u: number, d: number, slide: number): { u: number; d: number; points: number } {
  if (inHole(u, d)) return { u: 0, d: BOARD.holeD, points: 3 };
  // Short bags sometimes skid up onto the front lip.
  if (Math.abs(u) <= BOARD.halfWidth && d >= BOARD.front - 0.06 && d < BOARD.front && slide > 0.02)
    d = BOARD.front + 0.005;
  if (!onBoard(u, d)) return { u, d, points: 0 };
  for (let s = 0; s <= slide; s += 0.004) {
    if (inHole(u, d + s)) return { u: 0, d: BOARD.holeD, points: 3 };
  }
  const end = d + slide;
  if (end > BOARD.back) return { u, d: end + 0.04, points: 0 };
  return { u, d: end, points: 1 };
}

/** Cancellation scoring: only the difference in a frame counts, for whoever threw better. */
export function frameScore(bags: readonly Bag[]): { you: number; ricky: number } {
  let you = 0;
  let ricky = 0;
  for (const b of bags) {
    if (b.who === 'you') you += b.points;
    else ricky += b.points;
  }
  return you > ricky ? { you: you - ricky, ricky: 0 } : { you: 0, ricky: ricky - you };
}

/**
 * Cornhole against Lil' Ricky, the self-proclaimed Tri-County Champion. Tap to lock your
 * aim, tap again to lock the power. 3 in the hole, 1 on the board, first to 11.
 */
export class CornholeGame implements MiniGame {
  readonly title = "Cornhole vs. Lil' Ricky";
  readonly width = 360;
  readonly height = 260;
  phase: 'aim' | 'power' | 'flight' | 'pause' | 'frame' | 'over' = 'aim';
  score = { you: 0, ricky: 0 };
  frame = 1;
  bags: Bag[] = [];
  thrower: Thrower = 'you';
  aim = 0;
  power = 0;
  private t = 0;
  private flying: { from: number; u: number; d: number; slide: number; who: Thrower } | null = null;

  /** The bag in the air right now (where it'll land before it slides), and how far along it is. */
  get flight(): { u: number; d: number; who: Thrower; t: number } | null {
    const f = this.flying;
    return f && this.phase === 'flight' ? { u: f.u, d: f.d, who: f.who, t: Math.min(1, this.t / 0.7) } : null;
  }
  private lastPoints = -1;

  constructor(
    private readonly rng: Random,
    /** 0..1 how drunk you are: the aim marker gets a mind of its own. */
    private readonly drunk = 0,
  ) {}

  get winner(): Thrower | 'tie' | null {
    if (this.phase !== 'over') return null;
    if (this.score.you === this.score.ricky) return 'tie';
    return this.score.you > this.score.ricky ? 'you' : 'ricky';
  }

  get done(): boolean {
    return this.phase === 'over' && this.t > 1.4;
  }

  hint(controls: Controls): string {
    const act = { keys: 'Space/E', touch: 'Tap', pad: 'A' }[controls];
    return `${act} to lock your aim, ${act} again for power. 3 in the hole, 1 on the board. First to ${WIN_SCORE}.`;
  }

  private get thrown(): number {
    return this.bags.filter((b) => b.who === this.thrower).length;
  }

  update(dt: number, input: MiniInput): void {
    this.t += dt;
    switch (this.phase) {
      case 'aim':
        if (this.thrower === 'ricky') return this.rickyThrows();
        this.aim = Math.sin(this.t * 1.7) * 0.9 + Math.sin(this.t * 4.3) * 0.12 * this.drunk;
        if (input.pressed) {
          this.phase = 'power';
          this.t = 0;
        }
        return;
      case 'power':
        this.power = 0.5 - 0.5 * Math.cos(this.t * 2.4);
        if (input.pressed) this.throwBag('you', this.aim, this.power);
        return;
      case 'flight':
        if (this.t >= 0.7) this.land();
        return;
      case 'pause':
        if (this.t >= 0.6) this.next();
        return;
      case 'frame':
        if (this.t >= 1.6) this.nextFrame();
        return;
      default:
        return;
    }
  }

  private rickyThrows(): void {
    if (this.t < 0.5) return;
    const aim = clamp(this.rng.normal(0, 0.12), -1, 1);
    const power = clamp(this.rng.normal(0.74, 0.07), 0, 1);
    this.throwBag('ricky', aim, power);
  }

  throwBag(who: Thrower, aim: number, power: number): void {
    const drunk = who === 'you' ? this.drunk : 0;
    const spot = landing(aim, power);
    this.flying = {
      from: who === 'you' ? -0.2 : 0.2,
      u: spot.u + this.rng.normal(0, 0.03 + drunk * 0.05),
      d: spot.d + this.rng.normal(0, 0.02 + drunk * 0.03),
      slide: clamp((power - 0.5) * 0.12 + this.rng.range(-0.01, 0.03), 0, 0.09),
      who,
    };
    this.phase = 'flight';
    this.t = 0;
  }

  private land(): void {
    const f = this.flying;
    if (!f) return;
    const s = settle(f.u, f.d, f.slide);
    this.bags.push({ who: f.who, ...s });
    this.lastPoints = s.points;
    this.flying = null;
    this.phase = 'pause';
    this.t = 0;
  }

  private next(): void {
    const youDone = this.bags.filter((b) => b.who === 'you').length >= BAGS;
    const rickyDone = this.bags.filter((b) => b.who === 'ricky').length >= BAGS;
    this.t = 0;
    if (youDone && rickyDone) {
      const s = frameScore(this.bags);
      this.score.you += s.you;
      this.score.ricky += s.ricky;
      this.phase = 'frame';
      return;
    }
    this.thrower = this.thrower === 'you' && !rickyDone ? 'ricky' : youDone ? 'ricky' : 'you';
    this.phase = 'aim';
  }

  private nextFrame(): void {
    this.t = 0;
    const reached = this.score.you >= WIN_SCORE || this.score.ricky >= WIN_SCORE;
    if (reached || this.frame >= MAX_FRAMES) {
      this.phase = 'over';
      return;
    }
    this.frame++;
    this.bags = [];
    this.thrower = 'you';
    this.phase = 'aim';
  }

  // --- drawing ----------------------------------------------------------------------------

  private project(u: number, d: number): { x: number; y: number; s: number } {
    const k = clamp(d, 0, 1.15);
    return { x: this.width / 2 + u * lerp(180, 112, k), y: lerp(250, 56, k), s: lerp(1.7, 0.9, k) };
  }

  draw(g: CanvasRenderingContext2D, time: number): void {
    const W = this.width;
    const H = this.height;
    g.fillStyle = '#9ec4e0';
    g.fillRect(0, 0, W, 40);
    g.fillStyle = '#3f7a32';
    g.fillRect(0, 26, W, H);
    for (let i = 0; i < 9; i++) {
      g.fillStyle = i % 2 ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.04)';
      const a = this.project(-1.4, i / 8);
      const b = this.project(1.4, (i + 1) / 8);
      g.fillRect(0, b.y, W, a.y - b.y);
    }

    // The board, in perspective.
    const corners = [
      this.project(-BOARD.halfWidth, BOARD.front),
      this.project(BOARD.halfWidth, BOARD.front),
      this.project(BOARD.halfWidth, BOARD.back),
      this.project(-BOARD.halfWidth, BOARD.back),
    ];
    g.fillStyle = '#d8b98a';
    g.beginPath();
    corners.forEach((c, i) => (i ? g.lineTo(c.x, c.y) : g.moveTo(c.x, c.y)));
    g.closePath();
    g.fill();
    g.strokeStyle = '#7a5a3a';
    g.lineWidth = 2;
    g.stroke();
    const stripeA = this.project(-0.06, BOARD.front);
    const stripeB = this.project(0.06, BOARD.back);
    g.fillStyle = '#1d4e9e';
    g.fillRect(stripeA.x, stripeB.y, stripeB.x - stripeA.x, stripeA.y - stripeB.y);
    const hole = this.project(0, BOARD.holeD);
    g.fillStyle = '#120d08';
    g.beginPath();
    g.ellipse(hole.x, hole.y, 9 * hole.s * 1.2, 4.5 * hole.s * 1.2, 0, 0, Math.PI * 2);
    g.fill();

    // Bags (holed bags are gone down the hole).
    for (const b of this.bags) {
      if (b.points === 3) continue;
      const p = this.project(b.u, b.d);
      g.fillStyle = b.who === 'you' ? '#c62828' : '#2b6cb0';
      g.fillRect(p.x - 7 * p.s, p.y - 4 * p.s, 14 * p.s, 8 * p.s);
    }
    if (this.flying) {
      const f = this.flying;
      const k = clamp(this.t / 0.7, 0, 1);
      const p = this.project(lerp(f.from, f.u, k), lerp(0.02, f.d, k));
      const lift = Math.sin(k * Math.PI) * 70;
      g.fillStyle = f.who === 'you' ? '#c62828' : '#2b6cb0';
      g.save();
      g.translate(p.x, p.y - lift);
      g.rotate(time * 9);
      g.fillRect(-7 * p.s, -4 * p.s, 14 * p.s, 8 * p.s);
      g.restore();
    }

    // Aim marker and power meter.
    if (this.thrower === 'you' && (this.phase === 'aim' || this.phase === 'power')) {
      const target = this.project(landing(this.aim, 0.5).u, 0.86);
      g.strokeStyle = '#ffd400';
      g.lineWidth = 3;
      g.beginPath();
      g.moveTo(W / 2, H - 6);
      g.lineTo(target.x, target.y);
      g.stroke();
      g.fillStyle = '#ffd400';
      g.beginPath();
      g.arc(target.x, target.y, 5, 0, Math.PI * 2);
      g.fill();
    }
    if (this.phase === 'power') {
      g.fillStyle = 'rgba(0,0,0,0.45)';
      g.fillRect(W - 30, 60, 18, 170);
      const sweet = 170 * (1 - (BOARD.holeD - 0.45) / 0.6);
      g.fillStyle = 'rgba(80,220,80,0.5)';
      g.fillRect(W - 30, 60 + sweet - 12, 18, 24);
      g.fillStyle = '#ffd400';
      g.fillRect(W - 34, 60 + 170 * (1 - this.power) - 3, 26, 6);
    }

    // Scoreboard.
    g.fillStyle = 'rgba(0,0,0,0.55)';
    g.fillRect(8, 6, 196, 26);
    g.font = '15px Anton, Impact, sans-serif';
    g.textAlign = 'left';
    g.fillStyle = '#ff8a8a';
    g.fillText(`YOU ${this.score.you}`, 14, 25);
    g.fillStyle = '#8ac4ff';
    g.fillText(`RICKY ${this.score.ricky}`, 74, 25);
    g.fillStyle = '#fff';
    g.fillText(`FRAME ${this.frame}/${MAX_FRAMES}`, 140, 25);
    g.textAlign = 'right';
    g.fillText(
      this.thrower === 'you' ? `YOUR TOSS · ${BAGS - this.thrown} LEFT` : "RICKY'S TOSS",
      W - 10,
      25,
    );

    if (this.phase === 'pause' && this.lastPoints >= 0) {
      const text =
        this.lastPoints === 3 ? 'CORNHOLE!' : this.lastPoints === 1 ? 'ON THE BOARD' : 'IN THE GRASS';
      banner(g, text, W / 2, 120, this.lastPoints === 3 ? '#ffd400' : '#ffffff', 28);
    }
    if (this.phase === 'frame') {
      const s = frameScore(this.bags);
      const text = s.you > 0 ? `YOU +${s.you}` : s.ricky > 0 ? `RICKY +${s.ricky}` : 'WASH';
      banner(g, `END OF FRAME: ${text}`, W / 2, 120, '#ffffff', 26);
    }
    if (this.phase === 'over') {
      const w = this.winner;
      banner(
        g,
        w === 'you' ? 'YOU WIN!' : w === 'ricky' ? 'RICKY WINS' : 'PUSH',
        W / 2,
        120,
        w === 'you' ? '#ffd400' : '#ff8a6a',
        40,
      );
    }
  }
}
